import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import PremiumLocked from "../components/PremiumLocked";
import { BookOpen, Calendar, Clock, Check, Wand2 } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";

const Journal = () => {
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.listJournal();
        setEntries(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch { /* silent */ }
    })();
  }, []);

  // Group entries by date (newest dates first).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const key = e.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return Array.from(map.entries()); // [[date, [entries]], ...] already in createdAt-desc order from API
  }, [entries]);

  return (
    <div className="app-shell" data-testid="journal-page">
      <Header />
      <section className="px-6 pt-2 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Journal History</p>
        <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          What you set down
        </h2>
        <p className="text-sm text-[#5C7568] mt-2">
          {isPro ? `Your full reflection history · ${total} entries` : "Free shows today only. Pro unlocks full history."}
        </p>

        {entries.length === 0 ? (
          <div className="mt-6 rounded-3xl bg-white border border-[#E2EBE6] p-8 text-center" data-testid="journal-empty">
            <div className="w-12 h-12 rounded-full bg-[#F5D0D6]/40 flex items-center justify-center mx-auto">
              <BookOpen className="w-5 h-5 text-[#234B38]" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-[#5C7568] mt-3">
              No entries yet. Complete a reset and answer the In/Out prompts.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-6" data-testid="journal-entries">
            {grouped.map(([date, items]) => (
              <div key={date} data-testid={`journal-day-${date}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-3.5 h-3.5 text-[#234B38]" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#234B38]">{date}</p>
                  <span className="text-[10px] text-[#5C7568]">· {items.length} {items.length === 1 ? "entry" : "entries"}</span>
                </div>
                <div className="space-y-3">
                  {items.map((e, i) => (
                    <div
                      key={e.id || i}
                      className="rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.05)]"
                      data-testid={`journal-entry-${e.id || i}`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#5C7568]">
                          {e.time && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {e.time}</span>
                          )}
                          {e.state && (
                            <span className="flex items-center gap-1 text-[#234B38] font-semibold">
                              <Wand2 className="w-3 h-3" /> {e.state.replace("_", " ")}
                            </span>
                          )}
                          {e.breathwork_name && (
                            <span className="text-[#5C7568]">· {e.breathwork_name}</span>
                          )}
                        </div>
                        {e.completed && (
                          <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#234B38] text-white rounded-full px-2 py-0.5 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Done
                          </span>
                        )}
                      </div>
                      {e.answer_in ? (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#234B38]">In · {e.prompt_in}</p>
                          <p className="text-sm text-[#1A2E24] mt-1 leading-relaxed">{e.answer_in}</p>
                        </div>
                      ) : e.prompt_in ? (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#234B38]">In · {e.prompt_in}</p>
                          <p className="text-sm text-[#5C7568] italic mt-1">No response recorded</p>
                        </div>
                      ) : null}
                      {e.answer_out ? (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#234B38]">Out · {e.prompt_out}</p>
                          <p className="text-sm text-[#1A2E24] mt-1 leading-relaxed">{e.answer_out}</p>
                        </div>
                      ) : e.prompt_out ? (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#234B38]">Out · {e.prompt_out}</p>
                          <p className="text-sm text-[#5C7568] italic mt-1">No response recorded</p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!isPro && total > entries.length && (
          <div className="mt-6">
            <PremiumLocked
              title={`${total - entries.length} more entries hidden`}
              subtitle="Pro unlocks your full journal history with search and filters."
              onUnlock={() => navigate("/profile")}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default Journal;
